import { useEffect, useState } from "react";
import { playIntroSignalSound } from "../utils/sounds";

interface IntroScreenProps {
  onFinish: () => void;
}

export default function IntroScreen({ onFinish }: IntroScreenProps) {
  const [phase, setPhase] = useState<"enter" | "show" | "exit">("enter");
  const [ballPos, setBallPos] = useState({ x: -80, y: 50 });

  useEffect(() => {
    // 인트로 시작 시 시그널 효과음 재생
    const soundTimer = setTimeout(() => {
      playIntroSignalSound();
    }, 150);

    const t1 = setTimeout(() => setPhase("show"), 100);
    const t2 = setTimeout(() => setPhase("exit"), 2300);
    const t3 = setTimeout(() => onFinish(), 2900);
    return () => {
      clearTimeout(soundTimer);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onFinish]);

  // Ball rolling animation
  useEffect(() => {
    if (phase !== "show") return;
    let frame = 0;
    const interval = setInterval(() => {
      frame += 1;
      const progress = Math.min(frame / 60, 1);
      setBallPos({ x: -80 + progress * 160, y: 50 + Math.sin(progress * Math.PI * 4) * 8 });
    }, 16);
    return () => clearInterval(interval);
  }, [phase]);

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden transition-opacity duration-500 ${
        phase === "exit" ? "opacity-0" : "opacity-100"
      }`}
      style={{ background: "linear-gradient(160deg, #0a0a1a 0%, #1a1a3e 40%, #0d0d20 100%)" }}
    >
      {/* Speed lines background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 18 }).map((_, i) => (
          <div
            key={i}
            className="absolute h-px opacity-10"
            style={{
              background: "linear-gradient(90deg, transparent, #f59e0b, transparent)",
              top: `${5 + i * 5.5}%`,
              left: 0,
              right: 0,
              transform: `skewY(${i % 2 === 0 ? -1 : 1}deg)`,
              animationDelay: `${i * 0.05}s`,
            }}
          />
        ))}
        {/* Corner glow */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-orange-600/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      </div>

      {/* Main content */}
      <div
        className={`relative flex flex-col items-center transition-all duration-700 ${
          phase === "enter" ? "opacity-0 scale-75 translate-y-10" : "opacity-100 scale-100 translate-y-0"
        }`}
      >
        {/* Top badge */}
        <div className="mb-4 flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 rounded-full px-4 py-1">
          <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-amber-300 text-[10px] tracking-[0.3em] font-bold" style={{ fontFamily: "Orbitron, sans-serif" }}>
            LUCKY BALL GENERATOR
          </span>
        </div>

        {/* Logo block - italic/skewed like the reference */}
        <div className="relative mb-2">
          {/* Shadow layer */}
          <div
            className="absolute inset-0 translate-x-1.5 translate-y-1.5"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            <span className="text-[56px] font-black tracking-tight text-gray-900/80 italic select-none">
              PINBALL
            </span>
          </div>
          {/* Main text */}
          <div className="relative flex items-end gap-0">
            <span
              className="text-[56px] font-black tracking-tight italic leading-none select-none"
              style={{
                fontFamily: "Orbitron, sans-serif",
                background: "linear-gradient(180deg, #ffffff 0%, #d1d5db 50%, #9ca3af 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter: "drop-shadow(0 0 12px rgba(255,255,255,0.3))",
              }}
            >
              PiNBALL
            </span>
          </div>
        </div>

        {/* SIX 6 row */}
        <div className="relative flex items-center gap-3 -mt-2 mb-5">
          {/* SIX text */}
          <span
            className="text-[52px] font-black italic leading-none select-none"
            style={{
              fontFamily: "Orbitron, sans-serif",
              background: "linear-gradient(180deg, #fde68a 0%, #f59e0b 40%, #d97706 70%, #92400e 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 0 16px rgba(245,158,11,0.8)) drop-shadow(0 4px 8px rgba(0,0,0,0.5))",
              textShadow: "none",
            }}
          >
            SiX
          </span>
          {/* 6 badge - circle */}
          <div className="relative w-14 h-14 flex-shrink-0">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: "linear-gradient(135deg, #fde68a 0%, #f59e0b 40%, #b45309 100%)",
                boxShadow: "0 0 20px rgba(245,158,11,0.7), 0 0 40px rgba(245,158,11,0.3), inset 0 2px 4px rgba(255,255,255,0.3)",
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className="text-white font-black text-3xl italic leading-none"
                style={{
                  fontFamily: "Orbitron, sans-serif",
                  textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                }}
              >
                6
              </span>
            </div>
            {/* Ring */}
            <div className="absolute inset-0 rounded-full border-2 border-amber-300/50 animate-ping" style={{ animationDuration: "1.5s" }} />
          </div>
        </div>

        {/* Korean subtitle */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500/60" />
          <span className="text-amber-300/80 text-2xl tracking-widest font-bold">핀볼식스</span>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500/60" />
        </div>

        {/* Pinball rolling animation */}
        <div className="relative w-48 h-10 mb-5 overflow-hidden">
          {/* Track */}
          <div className="absolute bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-600/40 to-transparent rounded-full" />
          {/* Ball */}
          <div
            className="absolute w-8 h-8 rounded-full transition-none"
            style={{
              left: `${ballPos.x}%`,
              top: `${ballPos.y}%`,
              transform: "translate(-50%, -50%)",
              background: "radial-gradient(circle at 35% 35%, #fde68a, #f59e0b 50%, #92400e)",
              boxShadow: "0 0 12px rgba(245,158,11,0.8), 0 2px 6px rgba(0,0,0,0.5)",
            }}
          >
            {/* Shine */}
            <div className="absolute top-1 left-1.5 w-2 h-1.5 bg-white/50 rounded-full blur-[1px]" />
          </div>
          {/* Bumpers */}
          {[20, 50, 80].map((pos, i) => (
            <div
              key={i}
              className="absolute bottom-1 w-3 h-3 rounded-full border-2 border-amber-500/50 bg-amber-900/30"
              style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
            />
          ))}
        </div>

        {/* Loading dots */}
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="rounded-full animate-bounce"
              style={{
                width: i === 2 ? "10px" : "6px",
                height: i === 2 ? "10px" : "6px",
                background: i === 2 ? "#f59e0b" : "rgba(245,158,11,0.4)",
                animationDelay: `${i * 0.12}s`,
                animationDuration: "0.8s",
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom version text */}
      <div
        className={`absolute bottom-8 text-gray-600 text-[10px] tracking-widest transition-all duration-700 ${
          phase === "enter" ? "opacity-0" : "opacity-100"
        }`}
        style={{ fontFamily: "Orbitron, sans-serif" }}
      >
        v1.0
      </div>
    </div>
  );
}
